'use client';

import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faDownToLine,
  faFileExport,
  faXmark,
  faEllipsisV,
  IconDefinition,
} from '@fortawesome/pro-solid-svg-icons';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useLogging } from 'src/context/logging';
import { useNotification } from 'src/context/notification';
import { useAnalytics } from 'src/hooks/analytics';
import { useLazyGetTerritoryExportQuery } from 'src/api/redux';
import { Tooltip } from 'src/components';

interface DropdownOption {
  icon: IconDefinition;
  label: string;
  onClick: (e: React.MouseEvent) => void;
  variant?: 'default' | 'danger';
  disabled?: boolean;
  disabledReason?: string;
}

interface TableRowActionsProps {
  geographyId: string | number;
  showExport?: boolean;
  onBulkExport?: (type: 'provider' | 'site', id: string) => Promise<void>;
  className?: string;
  location?: string;
  isRegion?: boolean;
}

const TableRowActions: React.FC<TableRowActionsProps> = ({
  geographyId,
  showExport = true,
  onBulkExport,
  className = '',
  location = 'trends',
  isRegion = false,
}) => {
  const log = useLogging();
  const { setNotification } = useNotification();
  // Quarterly data is Elasticsearch-backed with no quarter granularity yet, so
  // bulk exports are blocked while a quarterly range is active.
  const { quarterlyTaData } = useFlags();
  const {
    selectedCodeSet = [],
    selectedGeographyObjects,
    selectedGeographies,
    granularity,
    setSelectedGeographyObjects,
    setSelectedGeographies,
  } = useAnalytics(location);

  const [triggerExport, { isLoading: isExportLoading }] =
    useLazyGetTerritoryExportQuery();

  const hasCodeSets = selectedCodeSet.length > 0;
  const isQuarterlyActive = !!quarterlyTaData && granularity === 'quarter';

  const handleRemoveGeography = (e: React.MouseEvent) => {
    e.stopPropagation();
    const idStr = geographyId.toString();
    const updatedGeographyObjects = selectedGeographyObjects.filter(
      (geo) => geo.id.toString() !== idStr
    );
    const updatedGeographyIds = selectedGeographies.filter(
      (id) => id.toString() !== idStr
    );
    setSelectedGeographyObjects(updatedGeographyObjects);
    setSelectedGeographies(updatedGeographyIds);
  };

  const handleTerritoryExport = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isExportLoading || !geographyId) return;
    try {
      const blob = await triggerExport({ territoryId: geographyId as number }).unwrap();

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `territory-${geographyId}-export.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setNotification({
        title: 'Success',
        message: 'Territory export downloaded successfully!',
        type: 'success',
      });
    } catch (err) {
      console.error('Territory export failed:', err);
      log.exception(`Error exporting territory: ${err}`);
      setNotification({
        title: 'Error',
        message: err?.error_message || 'Error requesting export',
        type: 'error',
      });
    }
  };

  const handleBulkExportProvider = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onBulkExport) return;
    try {
      await onBulkExport('provider', geographyId.toString());
    } catch (err) {
      console.error('Bulk export (provider) failed:', err);
      log.exception(`Error exporting territory analytics (provider): ${err}`);
    }
  };

  const handleBulkExportSite = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onBulkExport) return;
    try {
      await onBulkExport('site', geographyId.toString());
    } catch (err) {
      console.error('Bulk export (site) failed:', err);
      log.exception(`Error exporting territory analytics (site): ${err}`);
    }
  };

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const portalRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  const noCodeSetsReason = 'Select a code set to export';
  const regionExportDisabledReason = 'Territory export is not available for regions';
  const quarterlyBulkExportDisabledReason = 'Switch to yearly for bulk exports';

  const options: DropdownOption[] = [
    ...(showExport
      ? [
          {
            icon: faDownToLine,
            label: 'Export territory',
            onClick: handleTerritoryExport,
            variant: 'default' as const,
            disabled: isRegion || !hasCodeSets,
            disabledReason: isRegion
              ? regionExportDisabledReason
              : !hasCodeSets
              ? noCodeSetsReason
              : undefined,
          },
        ]
      : []),
    ...(onBulkExport
      ? [
          {
            icon: faFileExport,
            label: 'Bulk export Provider',
            onClick: handleBulkExportProvider,
            variant: 'default' as const,
            disabled: isQuarterlyActive || !hasCodeSets,
            disabledReason: isQuarterlyActive
              ? quarterlyBulkExportDisabledReason
              : !hasCodeSets
              ? noCodeSetsReason
              : undefined,
          },
          {
            icon: faFileExport,
            label: 'Bulk export Site',
            onClick: handleBulkExportSite,
            variant: 'default' as const,
            disabled: isQuarterlyActive || !hasCodeSets,
            disabledReason: isQuarterlyActive
              ? quarterlyBulkExportDisabledReason
              : !hasCodeSets
              ? noCodeSetsReason
              : undefined,
          },
        ]
      : []),
    {
      icon: faXmark,
      label: 'Remove geography',
      onClick: handleRemoveGeography,
      variant: 'danger' as const,
    },
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isClickInButton = dropdownRef.current?.contains(target);
      const isClickInDropdown = portalRef.current?.contains(target);

      if (!isClickInButton && !isClickInDropdown) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div
      className={`relative opacity-0 group-hover:opacity-100 transition-opacity ${className}`}
      ref={dropdownRef}
      data-testid="table-row-actions"
    >
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
          if (!isOpen && dropdownRef.current) {
            const rect = dropdownRef.current.getBoundingClientRect();
            setDropdownPosition({
              top: rect.bottom + 4,
              left: rect.right - 192, // 192px = w-48
            });
          }
        }}
        className="w-7 h-7 rounded-full flex items-center justify-center transition-all hover:scale-110 hover:bg-neutral-200"
        aria-label="Row actions"
        aria-haspopup="true"
        aria-expanded={isOpen}
        data-testid="table-row-actions-trigger"
      >
        <Icon
          icon={faEllipsisV}
          className="w-3.5 h-3.5 text-neutral-600 hover:text-neutral-700"
        />
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={portalRef}
            className="fixed w-48 bg-white rounded-md shadow-lg border border-neutral-200 py-1 z-[9999]"
            data-testid="table-row-actions-dropdown"
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
            }}
          >
            {options.map((option, index) => {
              const buttonContent = (
                <button
                  key={`${option.label}-${index}`}
                  onClick={(e) => {
                    if (!option.disabled) {
                      option.onClick(e);
                      setIsOpen(false);
                    }
                  }}
                  disabled={option.disabled}
                  className={`w-full flex items-center gap-1.5 px-4 py-2 text-sm text-left transition-colors ${
                    option.disabled
                      ? 'cursor-not-allowed text-neutral-400'
                      : option.variant === 'danger'
                      ? 'hover:bg-red-50 text-red-600'
                      : 'hover:bg-neutral-50 text-neutral-700'
                  }`}
                  data-testid={`table-row-actions-option-${option.label
                    .toLowerCase()
                    .replace(/\s+/g, '-')}`}
                >
                  <Icon
                    icon={option.icon}
                    className={`w-4 h-4 ${
                      option.disabled
                        ? 'text-neutral-400'
                        : option.variant === 'danger'
                        ? 'text-red-600'
                        : 'text-neutral-600'
                    }`}
                  />
                  <span>{option.label}</span>
                </button>
              );

              if (option.disabled && option.disabledReason) {
                return (
                  <Tooltip
                    key={`${option.label}-${index}`}
                    content={option.disabledReason}
                    position="top"
                    slotProps={{
                      containerClassName: '!bg-neutral-900 !text-white',
                    }}
                  >
                    {buttonContent}
                  </Tooltip>
                );
              }

              return buttonContent;
            })}
          </div>,
          document.body
        )}
    </div>
  );
};

export default TableRowActions;
